import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@nextui-org/react';
import {
  CtyButton,
  CtyShowToast,
  CtySpinner,
  InspectionChecklistEditor,
  checkType,
} from '@circuitry-ai/doc-ui';
import { itemDraftFromRecord } from './inspection-templates/inspection-templates-mappers';
import type {
  InspectionTemplate,
  InspectionTemplateItemDraft,
  InspectionTemplateItemRecord,
} from './inspection-templates/inspection-templates-types';
import { useInspectionTemplatesApi } from './inspection-templates/useInspectionTemplatesApi';
import { getSorErrorMessage } from './work-queues/useWorkQueuesApi';

interface InspectionTemplateEditModalProps {
  template: InspectionTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function InspectionTemplateEditModal({
  template,
  onClose,
  onSaved,
}: Readonly<InspectionTemplateEditModalProps>) {
  const { t } = useTranslation();
  const isOpen = template !== null;
  const { fetchTemplateItems, saveTemplateItems } = useInspectionTemplatesApi();

  const [originalItems, setOriginalItems] = useState<InspectionTemplateItemRecord[]>([]);
  const [items, setItems] = useState<InspectionTemplateItemDraft[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    setOriginalItems([]);
    setItems([]);
    setLoadError(null);
    if (!template) return;

    const controller = new AbortController();
    setIsLoading(true);
    fetchTemplateItems(template.id, controller.signal)
      .then((records) => {
        if (controller.signal.aborted) return;
        setOriginalItems(records);
        // Sections only group questions; they're kept as they are and not listed here
        setItems(records.filter((record) => record.data?.itemType !== 'SECTION').map(itemDraftFromRecord));
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setLoadError(getSorErrorMessage(error, t('Failed to load template')));
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [fetchTemplateItems, t, template]);

  const handleSave = useCallback(async () => {
    if (!template) return;
    if (items.some((item) => !item.title.trim())) {
      CtyShowToast(checkType.error, t('Every item needs a title'));
      return;
    }
    setIsSaving(true);
    try {
      await saveTemplateItems(template.id, items, originalItems);
      CtyShowToast(checkType.success, t('Template saved successfully'));
      onSaved();
    } catch (error) {
      CtyShowToast(checkType.error, getSorErrorMessage(error, t('Failed to save template')));
    } finally {
      setIsSaving(false);
    }
  }, [items, onSaved, originalItems, saveTemplateItems, t, template]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open && !isSaving) onClose();
      }}
      size="3xl"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-content1',
        header: 'border-b border-divider',
        footer: 'border-t border-divider',
      }}
    >
      <ModalContent>
        {(close) => (
          <>
            <ModalHeader className="flex items-center justify-between pr-12">
              <h2 className="text-lg font-semibold text-foreground">
                {template?.name || t('Edit Template')}
              </h2>
            </ModalHeader>
            <ModalBody className="p-1">
              {isLoading && (
                <div className="flex min-h-[320px] items-center justify-center">
                  <CtySpinner size="lg" />
                </div>
              )}
              {!isLoading && loadError && (
                <div className="px-6 py-10 text-center text-sm text-danger">{loadError}</div>
              )}
              {!isLoading && !loadError && (
                // shrink-0: an overflow-hidden flex child would otherwise squeeze to the body's
                // height and clip the rows, leaving ModalBody nothing to scroll
                <InspectionChecklistEditor items={items} onChange={setItems} className="shrink-0 overflow-hidden" />
              )}
            </ModalBody>
            <ModalFooter>
              <CtyButton
                variant="light"
                onPress={() => {
                  close();
                  onClose();
                }}
                isDisabled={isSaving}
              >
                {t('Cancel')}
              </CtyButton>
              <CtyButton
                color="primary"
                onPress={() => void handleSave()}
                isLoading={isSaving}
                isDisabled={isLoading || Boolean(loadError)}
              >
                {t('Save Template')}
              </CtyButton>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
